import React, { useContext } from 'react';
import throttle from 'lodash/throttle';
import shallowEqual from 'shallowequal';
import { breakpoints } from 'src/components/base';

type ViewportContextValue = {
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
};

const ViewportContext = React.createContext<ViewportContextValue>({});
ViewportContext.displayName = 'ViewportContext';

let queries: {
  smallOnly: MediaQueryList;
  mediumOnly: MediaQueryList;
  largeUp: MediaQueryList;
};

const getSizes = () => {
  if (!window) {
    // This should never happen (SSR always sends an initialValue) but just to be safe, here we go :)
    return { isMobile: false, isTablet: false, isDesktop: true };
  }

  if (!queries) {
    queries = {
      smallOnly: window.matchMedia(breakpoints.smallOnly),
      mediumOnly: window.matchMedia(breakpoints.mediumOnly),
      largeUp: window.matchMedia(breakpoints.largeUp),
    };
  }

  return {
    isMobile: queries.smallOnly.matches,
    isTablet: queries.mediumOnly.matches,
    isDesktop: queries.largeUp.matches,
  };
};

export const ViewportContextContainer = ({
  children,
  initialValue = undefined,
}: {
  children: React.ReactNode;
  initialValue?: ViewportContextValue;
}) => {
  // In SSR initialValue is guessed from the user agent
  const [state, setState] = React.useState(initialValue ?? getSizes());
  React.useEffect(() => {
    const onResize = throttle(() => {
      const sizes = getSizes();
      setState(s => {
        if (!shallowEqual(sizes, s)) {
          return sizes;
        }
        return s;
      });
    }, 200);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return (
    <ViewportContext.Provider value={state}>
      {children}
    </ViewportContext.Provider>
  );
};

export const useViewportContext = () => useContext(ViewportContext);

export default ViewportContext;
